import type {
  MultiPolygon, Polygon,
} from '~/_shared/types/polygon/polygon.types';
import type { BoundingBox } from '~/map/map/boundingBox';
import { type LatLng } from '../../types/latLng';

export const MAX_GOOGLE_LAT_LONG_DECIMALS = 10; //The max number of decimals returned by Google for lat long is 14 but 10 is mostly used

export const getCenterBetweenLatLngPositions = (position1: LatLng, position2: LatLng): LatLng => ({
  lat: (position1.lat + position2.lat) / 2,
  lng: (position1.lng + position2.lng) / 2,
});

export const areLatLngEqual = (latLng1: LatLng, latLng2: LatLng, precisionDecimals: number = 6) => (
  latLng1.lat.toFixed(precisionDecimals) === latLng2.lat.toFixed(precisionDecimals)
    && latLng1.lng.toFixed(precisionDecimals) === latLng2.lng.toFixed(precisionDecimals)
);

export const areBoundingBoxesEqual = (a: BoundingBox | null, b: BoundingBox | null, precisionDecimals: number = 6) => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return areLatLngEqual(a.getNorthEast(), b.getNorthEast(), precisionDecimals)
    && areLatLngEqual(a.getSouthWest(), b.getSouthWest(), precisionDecimals);
};

export const areLatLngPartiallyEqual = (latLng1: LatLng, latLng2: LatLng, precisionDecimals: number = MAX_GOOGLE_LAT_LONG_DECIMALS) => (
  latLng1.lat.toFixed(precisionDecimals) === latLng2.lat.toFixed(precisionDecimals)
    || latLng1.lng.toFixed(precisionDecimals) === latLng2.lng.toFixed(precisionDecimals)
);

export const latLngToFixedPrecision = (latLng: LatLng, precisionDecimals: number = 6): LatLng => ({
  lat: parseFloat(latLng.lat.toFixed(precisionDecimals)),
  lng: parseFloat(latLng.lng.toFixed(precisionDecimals)),
});

/**
 * Extracts the latitude and longitude from a given record.
 *
 * @param record - An object that contains latitude and longitude properties, along with any other properties.
 * @returns An object containing only the latitude (`lat`) and longitude (`lng`) properties.
 */
export const extractLatLng = (record: LatLng & Record<string, any>): LatLng => ({ lat: record.lat, lng: record.lng });

export const convertMultipolygonToWktFormat = (multiPolygon: MultiPolygon) => {
  if (multiPolygon.length === 0) {
    return 'MULTIPOLYGON EMPTY';
  }

  const polygonArray = multiPolygon.map(polygon => {
    return [
      convertPathToWktCoordinates(ensurePathCounterClockwise(polygon.path)),
      ...polygon.holes.map(hole => convertPathToWktCoordinates(ensurePathClockwise(hole))),
    ];
  });

  return `POLYGON(${polygonArray.join(',')})`;
};

const convertPathToWktCoordinates = (path: ReadonlyArray<LatLng>) => {
  if (path.length < 3) {
    throw new Error('A polygon requires at least 3 points.');
  }

  const normalizedPath = [...path];

  const first = normalizedPath[0];
  const last = normalizedPath[normalizedPath.length - 1];

  if (!first || !last) {
    throw new Error('Invalid path');
  }

  if (first.lat !== last.lat || first.lng !== last.lng) {
    normalizedPath.push(first);
  }

  // WKT uses: longitude latitude
  const coordinates = normalizedPath
    .map((p) => `${p.lng} ${p.lat}`)
    .join(',');

  return `(${coordinates})`;
};

const ensurePathClockwise = (path: ReadonlyArray<LatLng>): ReadonlyArray<LatLng> => {
  return getSignedRingArea(path) < 0 ? path : [...path].reverse();
};

const ensurePathCounterClockwise = (path: ReadonlyArray<LatLng>): ReadonlyArray<LatLng> => {
  return getSignedRingArea(path) > 0 ? path : [...path].reverse();
};

// Shoelace formula on (lng=x, lat=y). Returns 2x the planar signed area;
// only the sign is used to detect winding order so we can satisfy WKT's
// requirement that exterior rings are CCW (area > 0) and holes are CW (area < 0).
const getSignedRingArea = (path: ReadonlyArray<LatLng>) => {
  return path.reduce((area, point, index) => {
    const nextPoint = path[(index + 1) % path.length];

    if (!nextPoint) {
      return area;
    }

    return area + (point.lng * nextPoint.lat) - (nextPoint.lng * point.lat);
  }, 0);
};

export const getGooglePolygonPathsForPolygon = (polygon: Polygon): LatLng[][] => {
  return [polygon.path, ...polygon.holes].map(path => path.map(({ lat, lng }) => ({ lat, lng })));
};
